import { SalesPortalCheckoutResponse } from 'types';

/**
 * The post-purchase confirmation variants. Content is picked from the checkout response
 * flags — account/device state and the pre-selected-class outcome — and is never asked of
 * the buyer. Every class outcome is its own variant.
 */
export type ClassConfirmationVariant =
  | 'classBooked'
  | 'classAlreadyBooked'
  | 'classCredited'
  | 'classPromotedFromWaitlist'
  | 'classStillWaitlisted'
  | 'classFilled'
  | 'classNotRebookable'
  | 'classDailyLimitReached';

export type ConfirmationVariant = 'newUser' | 'hasApp' | 'linkedAthletePending' | ClassConfirmationVariant;

export type ConfirmationFlags = Pick<
  SalesPortalCheckoutResponse,
  'reservationOutcome' | 'waiverPending' | 'newAccount'
>;

type ClassOutcome = Exclude<SalesPortalCheckoutResponse['reservationOutcome'], 'none'>;

const CLASS_OUTCOME_VARIANTS: Record<ClassOutcome, ClassConfirmationVariant> = {
  booked: 'classBooked',
  already_booked: 'classAlreadyBooked',
  credited: 'classCredited',
  promoted_from_waitlist: 'classPromotedFromWaitlist',
  still_waitlisted: 'classStillWaitlisted',
  filled: 'classFilled',
  not_rebookable: 'classNotRebookable',
  daily_limit_reached: 'classDailyLimitReached',
};

const CLASS_VARIANTS = new Set<ConfirmationVariant>(Object.values(CLASS_OUTCOME_VARIANTS));

export function isClassConfirmationVariant(
  variant: ConfirmationVariant,
): variant is ClassConfirmationVariant {
  return CLASS_VARIANTS.has(variant);
}

export function deriveConfirmationVariant(flags: ConfirmationFlags): ConfirmationVariant {
  // A pre-selected class outcome is the headline; the pending-waiver line is then
  // rendered alongside it rather than replacing it.
  if (flags.reservationOutcome !== 'none') return CLASS_OUTCOME_VARIANTS[flags.reservationOutcome];
  if (flags.waiverPending) return 'linkedAthletePending';
  return flags.newAccount ? 'newUser' : 'hasApp';
}
